import type Stripe from 'stripe'
import { h, resolveComponent } from 'vue'
import type { Ref } from 'vue'
import {
  buildDataAddonAutoBuyFields,
  buildDataAddonPurchaseFields,
  createDataAddonRequestContext,
  type DataAddonRequestContext,
} from '@/utils/dataAddonPricing'
import { storeManagedText, storeRefusal, storeSubscriptionsLink, withServiceRow, type StoreManagement } from '@/utils/storeManaged'
import { toast } from '~/utils/sonner'

export interface PurchaseFlowOptions {
  // UpdateBilling component ref, used to auto-save the billing form during checkout
  billingEditor?: Ref<any>
  // Called when pay() is hit while logged out (pricing page opens the signup modal)
  onRequireLogin?: () => void
  // Called after a successful payment + data refresh; default navigates to the dashboard
  onSuccess?: () => void | Promise<void>
  // Where the embedded Stripe form mounts
  checkoutMountSelector?: string
}

// Payment execution flow shared by the pricing page and the purchase dialog.
// Selection & fee state lives in the usePricing store; this composable only
// owns the checkout lifecycle (agreements, billing, price confirm, channels,
// polling). Moved verbatim from pages/pricing.vue.
export function usePurchaseFlow(options: PurchaseFlowOptions = {}) {
  const { t: $t, locale } = useI18n()
  const $utils = useUtils()
  const user = useUser()
  const session = useVisitorSession()
  const service = useUserService()
  const pricing = usePricing()
  const isAdmin = computed(() => session.isAdmin)
  const DEFAULT_PROJECT_NAME = 'PandaBX'
  const IconComponent = resolveComponent('Icon')

  const pricingToast = (input: Parameters<typeof toast>[0]) => {
    if (typeof input === 'string') {
      return toast({ title: input, position: 'top-center' })
    }
    return toast({ ...input, position: input.position ?? 'top-center' })
  }

  let paymentPoll: any
  let expireTimeout: any
  let billingFlashTimer: ReturnType<typeof setTimeout> | undefined

  const confirming = ref(false)
  const showQr = ref(false)
  const qrData = ref('')
  const showBillingEditor = ref(false)
  const agreedToAll = ref(false)
  const confirmedInfo = ref({} as any)
  const confirmedDataAddonRequest = ref<DataAddonRequestContext | null>(null)
  const waitForPaymentConfirm = ref({})
  const showCurrencyWarning = ref(false)
  const currency = ref()

  const mode = computed(() => pricing.mode)
  const selectedCate = computed(() => pricing.cate)
  const selectedPlan = computed(() => pricing.plan as any)
  const selectedTime = computed(() => pricing.time)
  const promoCode = computed(() => pricing.promoCode)
  const topUpAmount = computed(() => pricing.topUpAmount)
  const selectedPaymentMethod = computed(() => pricing.selectedPaymentMethod as { currency?: string, name?: string })
  const extraPremium = computed(() => Number(pricing.extraPremium))
  const purchaseType = computed(() => pricing.purchaseType)
  const loggedIn = computed(() => session.loggedIn)

  const extraPremiumDataMode = computed(() => {
    return ['allplatform', 'home'].includes(selectedCate.value) && selectedTime.value === 0
  })
  const dataAddonPurchaseBlocked = computed(() => extraPremiumDataMode.value && !pricing.dataAddonQuote.ok)

  const exactAmountDue = computed(() => {
    const details = pricing.calculateFeeDetails({
      selectedPlan: selectedPlan.value || 0,
      purchaseType: purchaseType.value as any,
    })
    return details.amountDue
  })

  const requireBilling = computed(() => {
    return selectedPaymentMethod.value?.currency === 'EUR'
  })
  const billingComplete = computed(() => {
    return user.billing?.country?.length > 0
  })
  const showPaymentMethodSelector = computed(() => {
    return mode.value === 'topUp' || exactAmountDue.value > 0
  })
  const willShowBilling = computed(() => {
    return showBillingEditor.value || (requireBilling.value && loggedIn.value && !billingComplete.value)
  })
  const defaultPaymentParams = computed(() => {
    if (!session.isAdmin) {
      return {}
    }
    return {
      asUser: user.id,
    }
  })

  const needAgreement = computed(() => {
    return mode.value === 'buyService'
  })

  const visibleAgreements = computed(() => {
    const agreements = [
      {
        label: 'pricing.agreements.tos',
        linkParts: {
          before: 'pricing.agreements.tos_before_link',
          text: 'pricing.agreements.tos_link_text',
          after: 'pricing.agreements.tos_after_link',
        },
        to: '/terms-of-service',
      },
      { label: 'pricing.agreements.non_political' },
      { label: 'pricing.agreements.no_sharing' },
      { label: 'pricing.agreements.ban_country', cate: 'home' },
    ]

    return agreements
      .filter(option => !option.cate || option.cate === selectedCate.value)
      .map(option => ({
        ...option,
        text: $t(option.label),
        linkParts: option.linkParts
          ? {
              before: $t(option.linkParts.before),
              text: $t(option.linkParts.text),
              after: $t(option.linkParts.after),
            }
          : undefined,
      }))
  })

  function agreementText(option: { text: string, linkParts?: { before: string, text: string, after: string }, to?: string }) {
    if (!option.to || !option.linkParts) {
      return h('span', option.text)
    }

    return h('span', [
      option.linkParts.before,
      h('a', {
        href: option.to,
        target: '_blank',
        rel: 'noopener noreferrer',
        style: { color: 'inherit', textDecoration: 'none' },
      }, [
        h('span', { class: 'underline underline-offset-4' }, option.linkParts.text),
        h(IconComponent, {
          name: 'material-symbols:open-in-new-rounded',
          class: 'ml-1 inline-block h-3 w-3',
          style: { verticalAlign: '-0.125em' },
          'aria-hidden': 'true',
        }),
      ]),
      option.linkParts.after,
    ])
  }

  function agreementDialogBody() {
    return h('div', { class: 'grid gap-3 text-sm' }, [
      h('p', { class: 'text-muted-foreground' }, $t('pricing.agreements.please_agree.content')),
      h('ul', { class: 'grid gap-2 m-0 p-0 list-none' }, visibleAgreements.value.map(option =>
        h('li', { class: 'flex items-start gap-2' }, [
          h('span', { class: 'mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary' }),
          agreementText(option),
        ])
      )),
    ])
  }

  // fix selectedTime when upgrade
  watch(purchaseType, (type) => {
    if (type.type === 'upgrade' && ((pricing.currentService?.extra?.buy_length ?? 0) > 1 || pricing.currentService?.status === 'on')) {
      pricing.time = Number(pricing.currentService?.extra?.buy_length)
      console.log('selectedTime changed to:', pricing.time)
    }
  })

  tryOnScopeDispose(() => {
    if (paymentPoll) { clearInterval(paymentPoll) }
    if (expireTimeout) { clearTimeout(expireTimeout) }
    if (billingFlashTimer) { clearTimeout(billingFlashTimer) }
  })

  function popWarning() {
    if (!purchaseType.value.alert) {
      return null
    }
    return useDialog().confirm({
      title: $t(`${purchaseType.value.warning}.title`),
      body: $t(`${purchaseType.value.warning}.content`),
      confirmButtonText: $t(`${purchaseType.value.warning}.confirmButtonText`),
      cancelButtonText: $t(`${purchaseType.value.warning}.cancelButtonText`),
      defaultFocusButton: 'confirm',
      cancelButtonVariant: 'destructive',
    })
  }
  // A store owns this group's plan. Reload the services so the page swaps the
  // purchase UI for the same line, then show the line and the store link; the
  // reloaded row fills in what the refusal left out.
  async function showStoreManaged(refusal: StoreManagement) {
    confirming.value = false
    confirmedDataAddonRequest.value = null
    await service.getCurrentUserService({ refresh: true })
    const managed = withServiceRow(refusal, (service as any)[selectedCate.value])
    const link = storeSubscriptionsLink($t, managed.store)
    return useDialog().alert({
      title: storeManagedText($t, managed, locale.value),
      body: link
        ? h('a', { href: link.href, target: '_blank', rel: 'noopener noreferrer', class: 'font-semibold underline underline-offset-4' }, link.label)
        : undefined,
      confirmButtonText: $t('ok'),
    }).catch(() => { })
  }
  function confirmPriceWithServer(dryRun, dataAddonRequest: DataAddonRequestContext | null) {
    const opt = dataAddonRequest
      ? buildDataAddonPurchaseFields(dataAddonRequest, { dryRun: Boolean(dryRun) })
      : {
          dry_run: dryRun,
          plan_id: selectedPlan.value,
          promocode: promoCode.value,
          buy_length: selectedTime.value,
          all_pre: false,
        }
    return usePay.confirmPrice({
      userId: user.id,
      opt,
    })
  }
  function setConfirmedQuote(quote: Awaited<ReturnType<typeof confirmPriceWithServer>>) {
    const fee = quote?.fee
    const serverBalance = quote?.user_balance
    // Zero-fee responses omit the currency amounts in the services API.
    const eurAmount = fee === 0 && quote.eur_amount == null ? 0 : quote?.eur_amount
    if (!Number.isFinite(fee) || fee < 0) {
      throw new Error('Invalid payment quote: fee must be finite and non-negative')
    }
    if (!Number.isFinite(eurAmount) || eurAmount < 0) {
      throw new Error('Invalid payment quote: EUR amount must be finite and non-negative')
    }
    if (!Number.isFinite(serverBalance)) {
      throw new Error('Invalid payment quote: balance must be finite')
    }
    let amountDue = Math.max(0, (Math.round(eurAmount * 100) - Math.round(serverBalance * 100)) / 100)
    if (!Number.isFinite(amountDue)) {
      throw new Error('Invalid payment quote: amount due must be finite')
    }
    if (amountDue > 0 && amountDue < 1) amountDue = 1
    confirmedInfo.value = {
      ...quote,
      ori_rmb_amount: quote.rmb_amount,
      ori_eur_amount: eurAmount,
      eur_amount: amountDue,
      all_pre: quote.all_pre || false,
    }
  }
  function autoBuyFields() {
    const userReq = confirmedInfo.value.user_req || {}
    if (confirmedDataAddonRequest.value) {
      return buildDataAddonAutoBuyFields(confirmedDataAddonRequest.value)
    }
    return {
      auto_buy: userReq.plan_id,
      auto_buy_month: Number(confirmedInfo.value.buy_length),
      auto_buy_promo: userReq.promocode || userReq.promo_code,
      all_pre: userReq.all_pre,
    }
  }
  function confirmedRequestMode() {
    return confirmedDataAddonRequest.value?.mode ?? mode.value
  }
  async function afterSuccessPayment() {
    useDialog().alert({
      title: $t('billing.wait_for_payment.suc'),
      body: $t('billing.wait_for_payment.paid'),
      confirmButtonText: $t('ok'),
    })
    pricingToast({
      title: $t('billing.wait_for_payment.suc'),
    })
    await $utils.wait(3 * 1000)
    useDialog().close()
    console.log('stop polling....')
    if (paymentPoll) {
      clearInterval(paymentPoll)
    }
    pricing.invalidateAlreadyBought()
    user.invalidateAllowedPaymentMethods()
    await Promise.all([
      service.getCurrentUserService({ refresh: true }),
      user.get({ id: user.id!, reload: true }).then(userData => userData && user.setUserData(userData)),
    ])
    if (options.onSuccess) {
      return options.onSuccess()
    }
    return navigateTo(`/dashboard/${user.id}`)
  }
  async function scrollToBillingAddressForm() {
    await nextTick()
    const el = document.getElementById('billing-address-form')
    if (el) {
      const rect = el.getBoundingClientRect()
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight
      const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0)
      if (visibleHeight > Math.min(rect.height * 0.35, 160)) {
        return
      }
    }
    useScrollTo({ el: '#billing-address-form', offset: -120 })
  }
  async function flashBillingAddressForm(opts: { showToast?: boolean } = {}) {
    await scrollToBillingAddressForm()
    await nextTick()
    const el = document.getElementById('billing-address-form')
    if (el) {
      el.classList.remove('billing-attention')
      void el.offsetWidth
      el.classList.add('billing-attention')
      if (billingFlashTimer) {
        clearTimeout(billingFlashTimer)
      }
      billingFlashTimer = setTimeout(() => {
        el.classList.remove('billing-attention')
      }, 2200)
    }
    if (opts.showToast === false) {
      return
    }
    return pricingToast({
      title: $t('update_billing.notify_title'),
      description: $t('update_billing.notify'),
      variant: 'destructive',
    })
  }
  async function ensureBillingForCheckout() {
    if (!requireBilling.value) {
      return true
    }
    if (willShowBilling.value && options.billingEditor?.value?.updateBilling) {
      const saved = await options.billingEditor.value.updateBilling({ silentSuccess: true })
      console.log('checkout billing auto-save result:', saved, 'user.billing.country:', user.billing.country)
      if (!saved || !user.billing.country) {
        await flashBillingAddressForm({ showToast: false })
        return false
      }
      return true
    }
    if (!user.billing.country) {
      await flashBillingAddressForm()
      return false
    }
    return true
  }
  async function pay() {
    console.log('pay button clicked...')
    if (!session.loggedIn) {
      return options.onRequireLogin?.()
    }
    if (dataAddonPurchaseBlocked.value) {
      return pricingToast({
        title: $t('pricing.data_addon_unavailable'),
        variant: 'destructive',
      })
    }
    const billingReady = await ensureBillingForCheckout()
    if (!billingReady) {
      return
    }
    if (mode.value === 'topUp') {
      confirmedDataAddonRequest.value = null
      console.log('invoking topUp...')

      return invokePayment()
    }
    if (needAgreement.value && !agreedToAll.value) {
      const ret = await useDialog().confirm({
        title: $t('pricing.agreements.please_agree.title'),
        body: agreementDialogBody(),
        confirmButtonText: $t('pricing.agreements.agree_continue'),
        cancelButtonText: $t('cancel'),
        defaultFocusButton: 'confirm',
        confirmButtonVariant: 'success',
        dialogContentClass: 'md:w-[520px]',
      })
      if (ret !== 'confirm') {
        return pricingToast({
          title: $t('pricing.agreements.not_met'),
        })
      }
      agreedToAll.value = true
    }
    console.log('ok go...')
    if (purchaseType.value.alert) {
      const warning = purchaseType.value.warning
      const ret = await popWarning()
      if (ret !== 'confirm' && ret !== 'cancel') return
      if (ret === 'confirm') {
        if (warning === 'please_add_data_not_extend') {
          pricing.dataAddonPlanId = Number(pricing.currentService.plan_id)
          pricing.plan = selectedCate.value === 'home' ? 'home_quote' : 'allplatform_quote'
          pricing.time = 0
          pricing.allPre = false
          pricing.setDefaultDataAddonQuantity()
          return
        }
        if (warning === 'please_upgrade_not_extend') {
          pricing.plan = selectedCate.value === 'allplatform' ? 4 : 6
          return
        }
        if (warning === 'downgrade') return
      }
    }

    try {
      confirming.value = true
      confirmedDataAddonRequest.value = extraPremiumDataMode.value
        ? createDataAddonRequestContext(pricing.dataAddonQuote, { promoCode: promoCode.value })
        : null
      console.log('confirming price with server...')
      const oriConfirmedInfo = await confirmPriceWithServer(true, confirmedDataAddonRequest.value)
      console.log('oriConfirmedInfo is:', oriConfirmedInfo)
      setConfirmedQuote(oriConfirmedInfo)
      confirming.value = false
    } catch (e: any) {
      console.log('confirming price with server failed', e)
      const managed = storeRefusal(e)
      if (managed) return showStoreManaged(managed)
      confirming.value = false
      confirmedDataAddonRequest.value = null
      return useDialog().alert({
        title: $t('error.str'),
        body: e.data?.message === 'invalidPromo' ? $t('invalid_promo') : (e.data?.message || e.data || e.message),
        confirmButtonText: $t('ok'),
      }).catch(() => { })
    }
    // 直接购买
    console.log('confirmedInfo.value ', confirmedInfo.value)
    if (confirmedInfo.value.eur_amount === 0) {
      try {
        await confirmPriceWithServer(false, confirmedDataAddonRequest.value)
        return afterSuccessPayment()
      } catch (e: any) {
        let purchaseError = e
        if (e.data?.message?.startsWith('moneyNotEnough')) {
          try {
            confirming.value = true
            setConfirmedQuote(await confirmPriceWithServer(true, confirmedDataAddonRequest.value))
            if (confirmedInfo.value.eur_amount > 0) {
              confirming.value = false
              return invokePayment()
            }
            await confirmPriceWithServer(false, confirmedDataAddonRequest.value)
            confirming.value = false
            return afterSuccessPayment()
          } catch (retryError) {
            confirming.value = false
            purchaseError = retryError
          }
        }
        const managed = storeRefusal(purchaseError)
        if (managed) return showStoreManaged(managed)
        return useDialog().alert({
          title: $t('error.str'),
          body: purchaseError.data?.message || purchaseError.data || purchaseError.message || purchaseError,
          confirmButtonText: $t('ok'),
        }).catch(() => { })
      }
    }
    return invokePayment()
  }
  async function invokePayment() {
    const dataAddonRequest = confirmedDataAddonRequest.value
    const requestMode = confirmedRequestMode()
    useTryTrackEvent('payment_created', {
      user_id: user.id,
      session_id: session.id,
      mode: `${requestMode}`,
      payment_method: selectedPaymentMethod.value?.name,
      amount: requestMode === 'topUp' ? calcTopupFee() : confirmedInfo.value.eur_amount,
      plan_id: dataAddonRequest?.planId ?? selectedPlan.value,
      buy_length: dataAddonRequest?.amount ?? selectedTime.value,
      premium_data_amount: dataAddonRequest?.amount ?? extraPremium.value,
      data_addon_product: dataAddonRequest?.product,
      all_pre: dataAddonRequest?.allPre,
    })
    try {
      switch (selectedPaymentMethod.value.name) {
        case 'alipay_pc':
          return await await pingppCheckout('alipay_pc_direct')
        case 'alipay_mobile':
          return await pingppCheckout('alipay_wap')
        case 'yaband_wechat':
          return await yabandPay('Wechat')
        case 'yaband_alipay':
          return await yabandPay('Alipay')
        case 'stripe_card':
          return await stripeCheckout('card', { projectName: 'PandaBX' })
        case 'stripe_alipay':
          return await stripeCheckout('Alipay', { projectName: 'PandaBX' })
        case 'stripe_wechat':
          return await stripeCheckout('Wechat', { projectName: 'PandaBX' })
        case 'stripe_ideal':
          return await stripeCheckout('iDEAL', { projectName: 'PandaTL' })
        case 'stripe_pandatl_card':
          return await stripeCheckout('card', { projectName: 'PandaTL' })
        case 'paypal':
          return await paypalCheckout('paypal')
        case 'paymentwall':
          return await paymentwallCheckout({ ps: 'unionpay' })
        case 'paymentwall_cc':
          return await paymentwallCheckout({ ps: 'cc' })
        case 'paymentwall_pandapro':
          return await paymentwallCheckout({ ps: 'all', projectName: 'PandaTL' })
        case 'payop_alipay':
          return await payopCheckout('alipay')
        case 'payop_wechat':
          return await payopCheckout('wechat')
        case 'payop_unionpay':
          return await payopCheckout('unionpay')
        case 'payop_card':
          return await payopCheckout('card')
      }
    } catch (e: any) {
      console.log('invokePayment failed', e)
      const managed = storeRefusal(e)
      if (managed) return showStoreManaged(managed)
      useTryTrackEvent('error', {
        catched: true,
        error: e.message,
        action: 'invokePayment',
      })
      confirming.value = false
      return useDialog().alert({
        title: $t('error.str'),
        description: e.data?.message || e.data || e,
        body: `${JSON.stringify(isAdmin.value ? e.data?.error : e.data?.message)}`,
        confirmButtonText: $t('ok'),
      }).catch(() => { })
    }
  }
  function paymentwallCheckout({ ps = 'unionpay', projectName = DEFAULT_PROJECT_NAME }) {
    console.log('paymentwallCheckout:', ps)
    confirming.value = true
    showCurrencyWarning.value = false
    currency.value = 'eur'
    let opt = { ps, projectName } as {
      ps: string,
      projectName: string,
      amount?: number,
      eur?: number,
      auto_buy?: number | string,
      auto_buy_month?: number,
      auto_buy_promo?: string,
      all_pre?: boolean,
    }
    if (confirmedRequestMode() === 'buyService') {
      opt = {
        ...opt,
        amount: confirmedInfo.value.eur_amount,
        eur: confirmedInfo.value.eur_amount,
        ...autoBuyFields(),
      }
    } else if (confirmedRequestMode() === 'topUp') {
      const tmpAmount = calcTopupFee()
      opt = {
        ...opt,
        amount: tmpAmount,
        eur: tmpAmount,
      }
    }

    showCurrencyWarning.value = true

    usePay.createPaymentwall({ opt, params: defaultPaymentParams.value })
      .then((ret: any) => {
        console.log(ret)
        $utils.openSite(ret.directLink || ret.redirect)
        const _opt = {
          channel: 'paymentwall',
          id: ret.id,
          alternativeLink: ret.redirect || null,
        }
        waitForPayment(false, _opt)
      })
      .catch(paymentError)
      .finally(() => {
        confirming.value = false
      })
  }
  function paypalCheckout(channel) {
    console.log('paypalCheckout:', channel)
    confirming.value = true
    showCurrencyWarning.value = false
    currency.value = 'eur'
    let opt = {
      // apm: 'wechatpay'
    } as any
    if (confirmedRequestMode() === 'buyService') {
      opt = {
        ...opt,
        amount: confirmedInfo.value.eur_amount,
        eur: confirmedInfo.value.eur_amount,
        ...autoBuyFields(),
      }
    } else if (confirmedRequestMode() === 'topUp') {
      const tmpAmount = calcTopupFee()
      opt = {
        ...opt,
        amount: tmpAmount,
        eur: tmpAmount,
      }
    }
    usePay.createPaypal({ opt, params: defaultPaymentParams.value })
      .then((ret: any) => {
        $utils.openSite(ret.redirect)
        const _opt = {
          channel: `paypal`,
          id: ret.orderID,
        }
        waitForPayment(false, _opt)
      })
      .catch(paymentError)
      .finally(() => {
        confirming.value = false
      })
  }
  function payopCheckout(channel) {
    confirming.value = true
    showCurrencyWarning.value = false
    currency.value = 'eur'

    let opt = {
      paymentMethod: {
        alipay: 4501,
        wechat: 4546,
        unionpay: 376,
        card: 381
      }[channel],
      currency: currency.value?.toUpperCase() || 'EUR',
    } as any

    if (confirmedRequestMode() === 'buyService') {
      opt = {
        ...opt,
        amount: confirmedInfo.value.eur_amount,
        eur: confirmedInfo.value.eur_amount,
        ...autoBuyFields(),
      }
    } else if (confirmedRequestMode() === 'topUp') {
      const tmpAmount = calcTopupFee()
      opt = {
        ...opt,
        amount: tmpAmount,
        eur: tmpAmount,
      }
    }

    showCurrencyWarning.value = true

    console.log('---->createPayop opt is:', opt)

    usePay.createPayop(opt)
      .then((ret: any) => {
        console.log(channel, ':')
        console.log(ret)
        if (!ret.success) {
          return useDialog().alert({
            title: $t('error.str'),
            body: ret.error.message,
            confirmButtonText: $t('ok'),
          }).catch(() => { })
        }
        if (ret.barcode) {
          qrData.value = 'data:image/png;base64, ' + ret.barcode
          showQr.value = true
          return
        }
        $utils.openSite(ret.checkoutUrl)
      })
      .catch(paymentError)
      .finally(() => {
        confirming.value = false
      })
  }
  function stripeCheckout(channel, { projectName = DEFAULT_PROJECT_NAME }) {
    console.log('stripeCheckout:', channel)
    console.log('projectName:', projectName)
    confirming.value = true
    showCurrencyWarning.value = false
    currency.value = 'eur'
    if (['card', 'iDEAL'].includes(channel)) {
      return payWithCard(channel, { projectName })
    }

    let opt = {
      projectName,
    } as {
      ps: string,
      projectName: string,
      amount?: number,
      eur?: number,
      auto_buy?: number | string,
      auto_buy_month?: number,
      auto_buy_promo?: string,
      all_pre?: boolean,
    }

    if (confirmedRequestMode() === 'buyService') {
      opt = {
        ...opt,
        amount: confirmedInfo.value.eur_amount,
        eur: confirmedInfo.value.eur_amount,
        ...autoBuyFields(),
      }
    } else if (confirmedRequestMode() === 'topUp') {
      const tmpAmount = calcTopupFee()
      opt = {
        ...opt,
        amount: tmpAmount,
        eur: tmpAmount,
      }
    }

    showCurrencyWarning.value = true

    console.log('---->stripeCheckout opt is:', opt)

    usePay[`createStripe${channel}`]({ opt, params: defaultPaymentParams.value })
      .then((ret) => {
        if (ret.qr_code_url) {
          confirmedInfo.value.qr_url = ret.qr_code_url
          showQr.value = true
        } else {
          $utils.openSite(ret.url)
        }

        const _opt = {
          channel: `stripe_${channel}`.toLowerCase(),
          id: ret.source_id,
        }
        waitForPayment(ret.qr_code_url, _opt)
      })
      .catch(paymentError)
      .finally(() => {
        confirming.value = false
      })
  }
  function handleCharge(charge) {
    const ALIPAY_PC_DIRECT_URL = 'https://mapi.alipay.com/gateway.do'
    const hasOwn = {}.hasOwnProperty
    const channel = charge.channel
    const credential = charge.credential[channel]
    let baseURL = ALIPAY_PC_DIRECT_URL
    if (hasOwn.call(credential, 'channel_url')) {
      baseURL = credential.channel_url
    }
    if (!hasOwn.call(credential, '_input_charset')) {
      if (hasOwn.call(credential, 'service') && credential.service === 'create_direct_pay_by_user') {
        credential._input_charset = 'utf-8'
      }
    }
    const query = $utils.stringifyData(credential, channel, true)
    return baseURL + '?' + query
  }
  async function pingppCheckout(channel) {
    confirming.value = true
    showCurrencyWarning.value = false
    currency.value = 'rmb'

    let opt
    if (confirmedRequestMode() === 'buyService') {
      opt = {
        money: confirmedInfo.value.rmb_amount,
        rmb_amount: confirmedInfo.value.rmb_amount,
        amount: confirmedInfo.value.rmb_amount,
        ...autoBuyFields(),
        channel,
      }
    } else if (confirmedRequestMode() === 'topUp') {
      const tmpAmount = calcTopupFee()
      opt = {
        channel,
        money: $utils.eurToRmb(tmpAmount),
        amount: $utils.eurToRmb(tmpAmount),
      }
    }
    try {
      const ret: any = (await usePay.pingppCheckout({ opt, params: defaultPaymentParams.value }))
      if (['alipay_pc_direct', 'alipay_wap'].includes(channel)) {
        $utils.openSite(handleCharge(ret))
      } else {
        // QRCode
        confirmedInfo.value.qr_url = ret.credential.alipay_qr
      }
      const _opt = {
        channel: 'pingxx',
        id: ret.id,
      }
      waitForPayment(channel === 'alipay_qr', _opt)
    } catch (err) {
      paymentError(err)
    }
    confirming.value = false
  }
  async function checkPaymentStatus(opt) {
    // id, channel
    try {
      const data = await useAPI().get(`${opt.channel}/${opt.id}`) as string
      const res = data
      const sucArr = ['TRADE_FINISHED', 'complete', 'completed']
      if (sucArr.includes(res as string)) {
        clearInterval(paymentPoll)
        if (waitForPaymentConfirm.value) {
          useDialog().close()
        }
        return afterSuccessPayment()
      }
    } catch (e: any) {
      return console.log('checkPaymentStatus error:', e.data)
    }
  }
  function waitForPayment(isQr, opt) {
    const title = $t('billing.wait_for_payment.title')
    const msg = $t('billing.wait_for_payment.msg')

    if (paymentPoll) { clearInterval(paymentPoll) }

    paymentPoll = setInterval(() => {
      checkPaymentStatus(opt)
    }, 10 * 1000)

    let timeToExpire = 15 * 60

    if (opt.time_expire) { timeToExpire = opt.time_expire * 1000 - +new Date() }

    if (expireTimeout) { clearTimeout(expireTimeout) }

    expireTimeout = setTimeout(() => {
      if (paymentPoll) { clearInterval(paymentPoll) }
    }, timeToExpire * 1000)

    if (isQr) { return }
    let finalMsg: any = msg
    if (opt.alternativeLink) {
      finalMsg = h('p', null, [
        h('span', null, msg),
        h('br'),
        h('b', null, $t('billing.alternativeLink.a')),
        h('br'),
        h('a', { style: 'color: var(--alizarin)' }, $t('billing.do_not_scan_qr.a')),
      ])
    }
    waitForPaymentConfirm.value = useDialog().confirm({
      body: finalMsg,
      title,
      confirmButtonText: $t('billing.wait_for_payment.paid_button'),
      cancelButtonText: $t('billing.wait_for_payment.cancel'),
    })
      .then(() => {
        checkPaymentStatus(opt)
        pricingToast({
          title: $t('billing.wait_for_payment.waiting'),
        })
      })
      .catch(() => {
        console.log('stop polling....')
        if (paymentPoll) { clearInterval(paymentPoll) }
      })
  }
  async function payWithCard(channel, { projectName = DEFAULT_PROJECT_NAME }) {
    console.log('use stripe checkout:', channel)
    confirming.value = true

    let opt = {
      projectName,
      // uiMode: 'embedded'
    } as {
      ps: string,
      projectName: string,
      amount?: number,
      eur?: number,
      auto_buy?: number | string,
      auto_buy_month?: number,
      auto_buy_promo?: string,
      all_pre?: boolean,
      channel?: string,
      uiMode?: 'embedded' | 'hosted'
    }
    if (confirmedRequestMode() === 'buyService') {
      opt = {
        ...opt,
        amount: confirmedInfo.value.eur_amount,
        eur: confirmedInfo.value.eur_amount,
        ...autoBuyFields(),
        channel,
      }
    } else if (confirmedRequestMode() === 'topUp') {
      const tmpAmount = calcTopupFee()
      opt = {
        ...opt,
        eur: tmpAmount,
        amount: tmpAmount,
        channel,
      }
    }
    console.log('---->stripeCheckout opt is:', opt)
    const ret = await usePay.createStripeCheckoutSession({
      opt,
      params: defaultPaymentParams.value,
    }) as Stripe.Checkout.Session
    confirming.value = false
    let _opt
    if (ret.url) {
      $utils.openSite(ret.url)
      _opt = {
        channel: `stripe_card`,
        id: ret.id,
      }
      waitForPayment(false, _opt)
    }
    if (ret.ui_mode === 'embedded' && ret.client_secret) {
      openEmbeddedStripeForm(ret)
    }
  }
  function yabandPay(channel) {
    channel = channel.toLowerCase()
    confirming.value = true
    showCurrencyWarning.value = false
    currency.value = 'eur'

    let opt
    if (confirmedRequestMode() === 'buyService') {
      opt = {
        eur: confirmedInfo.value.eur_amount,
        amount: confirmedInfo.value.eur_amount,
        ...autoBuyFields(),
      }
    } else if (confirmedRequestMode() === 'topUp') {
      const tmpAmount = calcTopupFee()
      opt = {
        amount: tmpAmount,
        eur: tmpAmount,
      }
    }

    showCurrencyWarning.value = true

    usePay.createYaband({ opt: { channel, opt }, params: defaultPaymentParams.value })
      .then((ret: any) => {
        const { url } = ret
        confirmedInfo.value.qr_url = url
        showQr.value = true
        const _opt = {
          channel: `yaband`,
          id: ret.trade_id,
        }
        waitForPayment(url, _opt)
      })
      .catch(paymentError)
      .finally(() => {
        confirming.value = false
      })
  }
  function paymentError(err) {
    console.log(err)
    const managed = storeRefusal(err)
    if (managed) return showStoreManaged(managed)
    const e = err?.data?.message || err?.data
    pricingToast({
      title: err?.status >= 500 ? $t('server_error') : $t(`errors.${e}.title`),
      description: $t(`errors.${e}.message`),
      variant: 'destructive',
    })
  }
  function calcTopupFee() {
    const ret = Number(topUpAmount.value)
    if (ret < 1) {
      return 1
    }
    return ret
  }

  async function openEmbeddedStripeForm(checkoutSession: Stripe.Checkout.Session) {
    const stripeEmbeddedForm = useStripeEmbeddedForm()
    if (!checkoutSession) {
      console.log('openEmbeddedStripeForm: no session')
      return
    }
    if (!checkoutSession.metadata?.project_name) {
      console.log('openEmbeddedStripeForm: no project_name')
      return
    }
    // load stripe embedded form js and open the form
    const stripePK = useRuntimeConfig().public.stripePKs[checkoutSession.metadata?.project_name]
    const stripeInstance = await stripeEmbeddedForm.getStripeInstance(stripePK)
    const checkout = await stripeInstance.initEmbeddedCheckout({
      clientSecret: checkoutSession.client_secret,
    })
    console.log('checkout is', checkout)
    checkout.mount(options.checkoutMountSelector || '#checkout')
  }

  return {
    confirming,
    showQr,
    qrData,
    confirmedInfo,
    agreedToAll,
    showBillingEditor,
    visibleAgreements,
    agreementText,
    needAgreement,
    extraPremiumDataMode,
    dataAddonPurchaseBlocked,
    exactAmountDue,
    requireBilling,
    billingComplete,
    showPaymentMethodSelector,
    willShowBilling,
    pay,
    calcTopupFee,
    flashBillingAddressForm,
  }
}
